import BaseComponent from "./base_component_controller";
import { useResize } from "hb/insights/use_resize";
import { useChartConfig } from "hb/insights/use_chart_config";

export default class extends BaseComponent {
  static viewId = "billboard";

  static targets = ["billboardTemplate"];

  static get values() {
    return {
      ...BaseComponent.values,

      querySessionId: String,
      schema: Array,
      results: Array,
      chartConfig: Object,
    };
  }

  static configs = [
    { name: "groupType", default: "events" },
    "titleField",
    "titleURLField",
    "subtitleField",
    "valueField",
    "statusField",
  ];

  connect() {
    useChartConfig(this);
    this.billboardTemplate = this.compileElement(this.billboardTemplateTarget);

    super.connect();
  }

  resize() {
    this.findAll(".billboard-value span").forEach((text) => {
      const parent = text.parentElement;
      let minFontSize = 12;
      let maxFontSize = 120;
      let fontSize;

      parent.style.overflow = "visible";
      parent.style.textOverflow = "clip";
      parent.style.width = "auto";

      const parentWidth = parent.offsetWidth - 2;
      const parentHeight = parent.offsetHeight - 2;

      while (minFontSize <= maxFontSize) {
        fontSize = Math.floor((minFontSize + maxFontSize) / 2);
        text.style.fontSize = `${fontSize}px`;

        if (
          text.offsetWidth <= parentWidth &&
          text.offsetHeight <= parentHeight
        ) {
          minFontSize = fontSize + 1;
        } else {
          maxFontSize = fontSize - 1;
        }
      }

      text.style.fontSize = `${maxFontSize}px`;
      parent.style.overflow = "hidden";
      parent.style.textOverflow = "ellipsis";
      parent.style.width = "100%";
    });
  }

  outletsConnected() {
    super.outletsConnected();
    this.initQuery();
    useResize(this, { element: this.outletElement("chart") });
  }

  queryContextDefaults() {
    const { meta, results } = this.query;
    let config = {};

    // From Fields
    //
    if (results.length === 1) {
      config.groupType = "fields";

      // From Events
      //
    } else if (results.length <= 10) {
      config.groupType = "events";

      const [f1, f2] = meta.fields;
      if (f1 && f2) {
        config.titleField = f1;
        config.valueField = f2;
      } else if (f1) {
        config.titleField = "";
        config.valueField = f1;
      }
    }

    return config;
  }

  validate() {
    let { groupField, statusField, valueField } = this.config;
    let { results } = this.query;

    if (this.hasUnknownFields(groupField, statusField, valueField)) {
      this.errorMessage = "Unknown field selected";
    } else if (results?.length > 10) {
      this.errorMessage = "There are too many results";
    }
  }

  renderConfig() {
    const { meta } = this.query;

    [
      "subtitleField",
      "titleField",
      "titleURLField",
      "valueField",
      "statusField",
    ].forEach((name) => {
      let allFields;
      if (
        [
          "titleField",
          "statusField",
          "subtitleField",
          "titleURLField",
        ].includes(name)
      ) {
        allFields = ["__empty", ...meta.fields];
      } else {
        allFields = meta.fields;
      }

      this.find(`[data-input-name="${name}"] ul`).innerHTML = allFields
        .map((field) =>
          this.comboboxItemTemplate({
            classes: this[name] === field ? "current" : "",
            value: field,
            id: field,
            display: field,
            ...(field == "__empty" && {
              display: null,
              badge: "none",
            }),
          }),
        )
        .join("\n");
    });

    this.find(
      `input[name="groupType"][id="${this.config.groupType}"]`,
    ).checked = "true";
    this.find(`input[name="titleField"]`).value = this.config.titleField || "";
    this.find(`input[name="titleURLField"]`).value =
      this.config.titleURLField || "";
    this.find(`input[name="subtitleField"]`).value =
      this.config.subtitleField || "";
    this.find(`input[name="valueField"]`).value = this.config.valueField || "";
    this.find(`input[name="statusField"]`).value =
      this.config.statusField || "";
  }

  render() {
    const { results } = this.query;
    let final;

    if (this.config.groupType === "fields") {
      // Single row, multiple fields
      //
      const [result] = results;
      final = Object.entries(result.selectedFields).map(([title, value]) =>
        this.billboardTemplate({ title, value }),
      );
    } else if (this.config.groupType === "events") {
      // Multiple rows
      //
      final = results.map((result) => {
        if (this.config.titleField) {
          return this.billboardTemplate({
            title: result.field(this.config.titleField),
            titleURL: result.field(this.config.titleURLField),
            subtitle: result.field(this.config.subtitleField),
            value: result.field(this.config.valueField),
            status: result.field(this.config.statusField),
          });
        } else {
          return this.billboardTemplate({
            value: result.field(this.config.valueField),
          });
        }
      });
    }

    this.outletComponent("chart")?.setChartHtml(final.join("\n"));
    this.resize();
  }
}
